import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import {
    BarChart3,
    BookOpen,
    CalendarDays,
    CreditCard,
    GraduationCap,
    Heart,
    Presentation,
    Puzzle,
    Settings,
    Users,
    Wallet,
} from "lucide-react";

import TabSwitcher, { TabDefinition } from "../../../../shared/ui/tabs/TabSwitcher";
import { CenterType } from "../../../center";

interface Props {
    type: CenterType;
    /** id of the content these tabs switch, for aria-controls. */
    panelId?: string;
    /** The payments tab is there only while the centre has payments on. */
    showPayments?: boolean;
}

type CenterTab =
    | "/center"
    | "/center/team"
    | "/center/patients"
    | "/center/courses"
    | "/center/agenda"
    | "/center/reports"
    | "/center/payments"
    | "/center/tools"
    | "/center/subscription";

const SECTIONS: CenterTab[] = [
    "/center/team",
    "/center/patients",
    "/center/courses",
    "/center/agenda",
    "/center/reports",
    "/center/payments",
    "/center/tools",
    "/center/subscription",
];

/**
 * The administrator's own places. Organisational, all of them.
 *
 * The same segmented tabs as the practice settings, so the two administration
 * surfaces read as one product. Each tab is a route rather than local state:
 * a section can be linked to, and the browser's back button works.
 */
export default function CenterAdminNav({ type, panelId, showPayments = true }: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const tabs: TabDefinition<CenterTab>[] = [
        { id: "/center", label: t("center.nav.configuration"), icon: Settings },
        {
            id: "/center/team",
            label: type === "training" ? t("center.nav.trainers") : t("center.nav.team"),
            icon: type === "training" ? Presentation : Heart,
        },
        {
            id: "/center/patients",
            label: type === "training" ? t("center.nav.students") : t("center.nav.patients"),
            icon: type === "training" ? GraduationCap : Users,
        },
        // Only a training centre runs courses.
        { id: "/center/courses", label: t("center.nav.courses"), icon: BookOpen },
        { id: "/center/agenda", label: t("center.nav.agenda"), icon: CalendarDays },
        { id: "/center/reports", label: t("center.nav.reports"), icon: BarChart3 },
        { id: "/center/payments", label: t("center.nav.payments"), icon: Wallet },
        { id: "/center/tools", label: t("center.nav.tools"), icon: Puzzle },
        // What the centre pays, beside what it uses: the seats it has bought
        // and the modules on top of them are administration like the rest.
        {
            id: "/center/subscription",
            label: t("center.nav.subscription"),
            icon: CreditCard,
        },
    ].filter(
        (tab) =>
            (showPayments || tab.id !== "/center/payments") &&
            (type === "training" || tab.id !== "/center/courses")
    ) as TabDefinition<CenterTab>[];

    // Anything under a section belongs to it — a member's departure sits under
    // the team it is reached from — and everything else to Settings.
    const activeTab =
        SECTIONS.find((path) => pathname === path || pathname.startsWith(`${path}/`)) ?? "/center";

    return (
        <TabSwitcher
            tabs={tabs}
            activeTab={activeTab}
            onChange={(path) => navigate(path)}
            label={t("center.admin.title")}
            layoutId="activeTabCenterAdmin"
            panelId={panelId}
        />
    );
}
